<script>
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Modal from '@leaflink/stash/Modal.vue';
  import { storeToRefs } from 'pinia';

  import LinkABankButton from '@/components/LinkABankButton';
  import { ROUTE_NAMES } from '@/constants';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { track } from '@/track';
  import { locale } from '@/utils/i18n';

  export default {
    name: 'LinkABankCta',

    components: {
      Button,
      LinkABankButton,
      Icon,
      Modal,
    },

    emits: ['dismiss', 'submit', 'error'],

    props: {
      isOpen: Boolean,
    },

    computed: {
      disclaimerDate() {
        const localeOptions = {
          dateStyle: 'long',
        };

        return new Date(2023, 5, 30).toLocaleDateString(locale, localeOptions);
      },

      heading() {
        if (this.activeCompany?.seller) {
          return this.$t('termsAndConditions.linkABankCta.sellerHeading1');
        }

        return this.$t('termsAndConditions.linkABankCta.buyerHeading1');
      },
    },

    setup() {
      const companyStore = useCompanyStore();
      const { activeCompany } = storeToRefs(companyStore);

      const { user } = storeToRefs(useUserStore());

      return {
        activeCompany,
        user,
      };
    },

    methods: {
      onSubmit() {
        track('MP Plaid Link Bank - Clicked', {
          user_id: this.user.id,
          company_id: this.activeCompany.id,
          group_id: this.activeCompany.group?.id,
          placement: 'Onboarding',
        });

        this.$emit('submit');
        this.$emit('dismiss');
      },

      /**
       * Redirects the user to the payments dashboard if they skip linking or dismiss the modal.
       */
      goToPaymentsDashboard() {
        const query = {
          company_type: this.activeCompany.seller ? 'seller' : 'buyer',
          company_id: this.activeCompany.id,
        };
        const route = this.$router.resolve({ name: ROUTE_NAMES.PAYMENTS_ACCOUNT_SETTINGS, query });
        const target = this.$route.name === ROUTE_NAMES.CART ? '_blank' : '_self';

        // redirect the user to payments via /accounts/login to ensure their MP session is re-created, ensuring their
        // user meta is up to date and portal access meta is up to date!
        window.open(`/accounts/login?next=${encodeURIComponent(route.href)}`, target);

        this.$emit('dismiss');
      },

      onCancel() {
        track('MP Plaid Link Bank - Skipped', {
          user_id: this.user.id,
          company_id: this.activeCompany.id,
          group_id: this.activeCompany.group?.id,
        });

        this.goToPaymentsDashboard();
      },

      onError() {
        track('MP Plaid Link Bank - Error', {
          user_id: this.user.id,
          company_id: this.activeCompany.id,
          group_id: this.activeCompany.group?.id,
          placement: 'Onboarding',
        });

        this.$emit('error');
      },
    },
  };
</script>

<template>
  <Modal size="narrow" :class="$style.wrapper" :open="isOpen" hide-close @dismiss="goToPaymentsDashboard">
    <div class="tw-flex tw-items-center tw-rounded-t" :class="$style.header">
      <img
        :src="'/static/img/leaflink-payments-white.svg'"
        :class="$style['payments-logo']"
        :alt="$t('common.llPayments')"
      />
    </div>

    <div class="tw-text-center" :class="$style.body">
      <h3>{{ heading }}</h3>

      <div class="tw-my-6 tw-flex tw-justify-center tw-items-center">
        <Icon class="tw-text-ice-900 tw-mr-1.5" name="lock" size="dense" />
        <span class="tw-font-semibold tw-text-ice-900" :class="$style['secure-text']">
          {{ $t('termsAndConditions.linkABankCta.linkYourBankSecurelyWith') }}
        </span>
        <img
          class="tw-inline-block tw-mr-1.5"
          :src="'/static/img/plaid-logo.svg'"
          :class="$style['plaid-img']"
          alt="Plaid"
        />
      </div>

      <LinkABankButton @error="onError" @submit="onSubmit" />

      <Button inline @click="onCancel">
        {{ $t('termsAndConditions.linkABankCta.dismissButton') }}
      </Button>
    </div>
  </Modal>
</template>

<style lang="scss" module>
  $header-height: space(5);

  .wrapper {
    // LLModal header
    header {
      display: none;
    }

    // LLModal body
    header + div {
      border-top-left-radius: $border-radius;
      border-top-right-radius: $border-radius;
      padding: 0;
    }
  }

  .header {
    background: var(--color-purple);
    height: $header-height;
    justify-content: center;
    position: relative;

    .close {
      background: transparent;
      color: #fff;
      height: space(5);
      position: absolute;
      right: 0;
      top: 0;
      width: space(5);
    }
  }

  .body {
    padding: space(3) var(--grid-gutter);
  }

  .payments-logo {
    height: 20px;
  }

  // The following 2 classes are required to render an exact match to the figma design.
  .secure-text {
    height: 15px;
  }

  .plaid-img {
    margin-top: 2px;
  }
</style>
